<template>
	<div class="h-full overflow-y-scroll bg-gray-100">
		<AppPageHeader
			class="px-0"
			:back-link
			content-classes="max-w-[80rem] px-10"
		>
			<img
				v-if="lender?.logo"
				class="object-contain h-8 w-24"
				:src="`/logos/${lender.logo}`"
				:alt="lender.label"
			/>
			<DashboardMetric
				v-if="loanApplication?.kenmerk"
				label="Kenmerk"
				:value="loanApplication?.kenmerk"
				class="pt-4"
			/>
		</AppPageHeader>

		<ApiErrorCard v-if="apiError" :error="apiError" class="max-w-sm m-8" />

		<main class="grid grid-cols-5 gap-5 p-8 max-w-7xl mx-auto">
			<div class="col-span-3 space-y-5">
				<LoanApplicationOfferCard :application-id="loanApplicationId" />
				<LoanApplicationValidationCard :form-has-issues="hasFormIssues" />
				<LoanApplicationRequestedDocumentsCard
					:application-id="loanApplicationId"
				/>
			</div>

			<div class="col-span-2 space-y-5">
				<LoanApplicationActionsCard
					:application-id="loanApplicationId"
					:dossier-display-id
					:possible-actions="summary?.actions"
					@revise="onRevisionClick"
					@send="onSubmitClick"
				/>
				<LoanApplicationStatusCard
					v-if="applicationStatus"
					:status="applicationStatus"
					:status-message="loanApplication?.hdnDossier?.statusMessage"
				/>
			</div>
		</main>

		<LoanApplicationSubmitModal
			v-model="showSubmitModal"
			:loan-application-id
			@success="refreshApplicationSummary()"
		/>
		<LoanApplicationRevisionModal
			v-if="showRevisionModal"
			:loading="loadingRevision"
			:error="revisionError"
			@submit="makeRevision"
			@cancel="onRevisionCancel"
		/>
	</div>
</template>

<script lang="ts" setup>
import type { LoanApplicationSummary } from "@/generatedApi";
import { api } from "@/loanLinkApi";
import type { ApiError } from "@/models/ApiError";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useIntervalFn } from "@vueuse/core";
import { getProviderLabel } from "@/composables/useProvider";
import { useProviderLenders } from "@/composables/useProviderLenders";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";

const router = useRouter();
const route = useRoute();

const dossierDisplayId = computed(
	() => route.params.dossierDisplayId as string,
);
const loanApplicationId = computed(
	() => route.params.loanApplicationId as string,
);

const { data: loanApplication } = useQuery(() =>
	queries.loanApplications.byId({ loanApplicationId: loanApplicationId.value }),
);
const { getLenderAsset, getLenderOption } = useProviderLenders();

const applicationStatus = computed(() => {
	if (!loanApplication.value?.hdnDossier?.status) {
		if (summary.value?.sentAt) {
			return "sent";
		}
		return "draft";
	}
	return loanApplication.value?.hdnDossier?.status;
});
const lender = computed(() => {
	const option = getLenderOption(
		loanApplication.value?.hdnDossier?.providerCode ?? undefined,
	);
	if (option) {
		return option;
	}

	const provider = loanApplication.value?.hdnDossier?.provider;
	if (!provider) {
		return undefined;
	}

	const lender = getLenderAsset(provider.name);

	return {
		value: provider.name,
		label: getProviderLabel(provider),
		icon: lender?.icon ?? undefined,
		logo: lender?.logo ?? undefined,
	};
});

const backLink = {
	label: "Terug naar alle aanvraagen",
	to: { name: `LoanApplicationOverviewPage` },
};

const apiError = ref(null);
const summary = ref<LoanApplicationSummary | null>(null);

async function refreshApplicationSummary() {
	await api.loanApplication
		.getApplicationSummary(loanApplicationId.value)
		.then((res) => {
			summary.value = res.data;
			apiError.value = null;
		})
		.catch((res) => (apiError.value = res.error));
}

const hasFormIssues = computed(() => {
	if (summary.value === null) {
		return undefined;
	}
	return Object.keys(summary.value.formIssues).length > 0;
});

watch(loanApplicationId, () => {
	summary.value = null;
	void refreshApplicationSummary();
});

onMounted(async () => refreshApplicationSummary());
useIntervalFn(() => {
	void refreshApplicationSummary();
}, 10 * 1000);

// submit modal
const showSubmitModal = ref(false);
const onSubmitClick = () => (showSubmitModal.value = true);

// revision modal
const showRevisionModal = ref(false);
const loadingRevision = ref(false);
const revisionError = ref<null | ApiError>(null);

async function makeRevision() {
	loadingRevision.value = true;
	await api.loanApplication
		.createApplicationRevision(loanApplicationId.value)
		.then(async () => {
			await refreshApplicationSummary();
			await router.push({ name: "LoanApplicationEditPage" });
			showRevisionModal.value = false;
		})
		.catch(async (res) => {
			revisionError.value = await res.json();
		})
		.finally(() => {
			loadingRevision.value = false;
		});
}

function onRevisionCancel() {
	revisionError.value = null;
	showRevisionModal.value = false;
}

function onRevisionClick() {
	showRevisionModal.value = true;
}
</script>
